import {
  CHART_COMPARE_DASH,
  CHART_COMPARE_NEUTRAL,
  chartFaded,
} from './chartTokens';

/**
 * ТЗ 0103 — данные графика «период против периода» без canvas: построитель
 * чистый и тестируется сам, компонент только отдаёт его chart.js.
 *
 * Правила (design-erp §2.2):
 *  - выравнивание — по НОМЕРУ корзины (день i против дня i), а не по дате на
 *    общей оси: у текущей и прошлой корзины разные даты, и подсказка
 *    называет обе;
 *  - счётчики — сгруппированные столбики: текущие сплошные, сравнение того
 *    же цвета на 35 %; деньги и доли — сравнение пунктиром;
 *  - `null` — разрыв (корзины сравнения нет: в августе 31 день, в сентябре
 *    30), а не ноль;
 *  - неполная корзина (обрезанная крайняя неделя, текущий месяц) — бледнее
 *    и с пометкой в подсказке.
 */

export interface IPeriodCompareBucket {
  /** Подпись оси: «14.09». */
  label: string;
  /** Подпись в подсказке: «Пн 14.09» или «14.09–20.09». */
  title: string;
  /** Подпись корзины сравнения в подсказке; `null` — её нет. */
  compareTitle?: string | null;
  current: number | null;
  previous?: number | null;
  /** Корзина собрана не целиком. */
  partial?: boolean;
  /** Значения стека текущих столбиков — по `stackSeries`. */
  stack?: ReadonlyArray<number | null>;
  /**
   * Повторное ревью 0103 (A3): дней в корзине и в корзине сравнения. Длины
   * разные (4 против 6 дней, 30 против 31) — процента в подсказке нет;
   * неизвестно — сравниваем как раньше.
   */
  days?: number | null;
  compareDays?: number | null;
  /** Корзина сравнения собрана не целиком — бледнее, как текущая. */
  comparePartial?: boolean;
}

export interface IPeriodCompareSeries {
  label: string;
  color: string;
}

export interface IPeriodCompareLine {
  label: string;
  color: string;
  /** По корзинам; `null` — разрыв. */
  values: ReadonlyArray<number | null>;
}

export type PeriodCompareStyle = 'bars' | 'line';

export interface IPeriodCompareInput {
  buckets: ReadonlyArray<IPeriodCompareBucket>;
  currentLabel: string;
  /** Подпись ряда сравнения; `null` — сравнения нет. */
  compareLabel?: string | null;
  color: string;
  /** Столбиками (счётчики) или пунктиром (деньги, доли). */
  compareStyle?: PeriodCompareStyle;
  /** Стек текущих: подписи и цвета слоёв. Сравнение тогда — линия итога. */
  stackSeries?: ReadonlyArray<IPeriodCompareSeries>;
  /** Линия на правой оси (продажи поверх лидов). */
  line?: IPeriodCompareLine | null;
}

export interface IPeriodCompareDataset {
  type: 'bar' | 'line';
  label: string;
  data: Array<number | null>;
  backgroundColor: string | string[];
  borderColor?: string;
  borderWidth?: number;
  borderDash?: number[];
  borderRadius?: number;
  pointRadius?: number;
  tension?: number;
  stack?: string;
  order: number;
  yAxisID: 'y' | 'y1';
  maxBarThickness?: number;
  spanGaps?: boolean;
  /** Что за ряд — для подсказки. */
  role: 'current' | 'stack' | 'compare' | 'line';
}

export interface IPeriodCompareData {
  labels: string[];
  datasets: IPeriodCompareDataset[];
  /** Есть ряд сравнения. */
  hasCompare: boolean;
  /** Есть правая ось. */
  rightAxis: boolean;
}

/** Значение сравнения корзины: нет поля — `null`. */
const previousOf = (bucket: IPeriodCompareBucket): number | null =>
  bucket.previous === undefined ? null : bucket.previous;

/** Сумма стека корзины; все слои `null` — `null`. */
export const periodCompareStackTotal = (
  stack: ReadonlyArray<number | null> | undefined,
): number | null => {
  if (!stack || stack.every((value) => value === null)) return null;
  return stack.reduce<number>((sum, value) => sum + (value ?? 0), 0);
};

/**
 * Изменение текущего к сравнению в процентах, одна цифра после запятой;
 * `null` — сравнивать нечего (любая сторона `null` или было 0).
 */
export const periodCompareDeltaPct = (
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null => {
  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined ||
    previous === 0
  ) {
    return null;
  }
  return Math.round(((current - previous) * 1000) / Math.abs(previous)) / 10;
};

/**
 * Длины корзины и корзины сравнения различаются (обе известны): процент
 * между ними вводит в заблуждение — 4 дня против 6 — это не «−33 %».
 */
export const periodCompareLengthsDiffer = (
  bucket: Pick<IPeriodCompareBucket, 'days' | 'compareDays'>,
): boolean =>
  typeof bucket.days === 'number' &&
  typeof bucket.compareDays === 'number' &&
  bucket.days !== bucket.compareDays;

/** Доля прозрачности неполной корзины (текущей и сравнения). */
const PARTIAL_ALPHA = 0.55;

export const periodCompareData = ({
  buckets,
  currentLabel,
  compareLabel = null,
  color,
  compareStyle = 'bars',
  stackSeries,
  line = null,
}: IPeriodCompareInput): IPeriodCompareData => {
  const labels = buckets.map((bucket) => bucket.label);
  const hasCompare = Boolean(compareLabel);
  const stacked = Boolean(stackSeries && stackSeries.length);
  const datasets: IPeriodCompareDataset[] = [];

  if (stacked && stackSeries) {
    stackSeries.forEach((series, layer) => {
      datasets.push({
        type: 'bar',
        role: 'stack',
        label: series.label,
        data: buckets.map((bucket) => bucket.stack?.[layer] ?? null),
        backgroundColor: buckets.map((bucket) =>
          bucket.partial
            ? chartFaded(series.color, PARTIAL_ALPHA)
            : series.color,
        ),
        borderRadius: 2,
        stack: 'current',
        order: 2,
        yAxisID: 'y',
        maxBarThickness: 36,
      });
    });
  } else {
    datasets.push({
      type: 'bar',
      role: 'current',
      label: currentLabel,
      data: buckets.map((bucket) => bucket.current),
      backgroundColor: buckets.map((bucket) =>
        bucket.partial ? chartFaded(color, PARTIAL_ALPHA) : color,
      ),
      borderRadius: 2,
      stack: 'current',
      order: 2,
      yAxisID: 'y',
      maxBarThickness: 36,
    });
  }

  if (hasCompare && compareLabel) {
    // Стек сравнивается одной пунктирной линией итога: стопка бледных
    // столбиков рядом со стопкой ярких не читается.
    const asLine = stacked || compareStyle === 'line';
    const values = buckets.map(previousOf);
    // Итог стека — нейтральным сланцем: цвет слоя читался бы ещё слоем.
    const lineColor = stacked ? CHART_COMPARE_NEUTRAL : color;
    datasets.push(
      asLine
        ? {
            type: 'line',
            role: 'compare',
            label: compareLabel,
            data: values,
            // Неполная корзина сравнения — бледной точкой, как текущая.
            backgroundColor: buckets.map((bucket) =>
              bucket.comparePartial
                ? chartFaded(lineColor, PARTIAL_ALPHA)
                : lineColor,
            ),
            borderColor: lineColor,
            borderWidth: 2,
            borderDash: CHART_COMPARE_DASH,
            pointRadius: 2,
            tension: 0.2,
            order: 1,
            yAxisID: 'y',
            spanGaps: false,
          }
        : {
            type: 'bar',
            role: 'compare',
            label: compareLabel,
            data: values,
            backgroundColor: buckets.map((bucket) =>
              bucket.comparePartial
                ? chartFaded(color, 0.35 * PARTIAL_ALPHA)
                : chartFaded(color),
            ),
            borderRadius: 2,
            stack: 'previous',
            order: 3,
            yAxisID: 'y',
            maxBarThickness: 36,
          },
    );
  }

  if (line) {
    datasets.push({
      type: 'line',
      role: 'line',
      label: line.label,
      data: buckets.map((_, index) => line.values[index] ?? null),
      backgroundColor: line.color,
      borderColor: line.color,
      borderWidth: 2.5,
      pointRadius: 3,
      tension: 0.2,
      order: 0,
      yAxisID: 'y1',
      spanGaps: false,
    });
  }

  return { labels, datasets, hasCompare, rightAxis: Boolean(line) };
};
